import React from 'react';
import { useAdminStats } from '../../hooks/useInvestQueries';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';
import AppLayout from '../../components/layouts/AppLayout';
import ResponsiveNavbar from '../../components/ResponsiveNavbar';
import { Users, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading: loading, error } = useAdminStats();

  if (error) {
    toast.error('Failed to load stats');
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="admin-dashboard">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={true} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-[#94A3B8] text-sm sm:text-base">Platform overview and key metrics</p>
          </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : (
          <div>
            {/* Stats Grid */}
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
              <div className="premium-stat-card p-6" data-testid="stat-total-investors">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Investors</span>
                  <Users className="w-5 h-5 text-[#FFD76A]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{stats?.total_investors || 0}</p>
              </div>

              <div className="premium-stat-card p-6" data-testid="stat-pending-applications">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Pending Apps</span>
                  <Clock className="w-5 h-5 text-[#FFB347]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{stats?.pending_applications || 0}</p>
              </div>

              <div className="premium-stat-card p-6" data-testid="stat-total-assets">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Assets</span>
                  <TrendingUp className="w-5 h-5 text-[#FFD76A]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{stats?.total_assets || 0}</p>
              </div>

              <div className="premium-stat-card p-6" data-testid="stat-pending-commitments">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Pending Commits</span>
                  <Clock className="w-5 h-5 text-[#FFB347]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{stats?.pending_commitments || 0}</p>
              </div>

              <div className="premium-stat-card p-6" data-testid="stat-pending-payouts">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Pending Payouts</span>
                  <DollarSign className="w-5 h-5 text-[#F5C84C]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-3xl font-bold golden-text">{stats?.pending_payouts || 0}</p>
              </div>

              <div className="premium-stat-card p-6" data-testid="stat-total-committed">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[#94A3B8] text-sm uppercase tracking-wider">Total Committed</span>
                  <DollarSign className="w-5 h-5 text-[#F5C84C]" />
                </div>
                <p className="font-['JetBrains_Mono'] text-2xl font-bold golden-text">${((stats?.total_committed || 0) / 1000).toFixed(0)}k</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button onClick={() => navigate('/admin/investors')} className="group premium-card p-6 hover:scale-[1.02] transition-all text-left cursor-pointer relative overflow-hidden" data-testid="goto-investors-btn">
                <div className="absolute top-0 right-0 p-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <Users className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div className="relative z-10">
                  <h3 className="font-['Outfit'] text-xl font-bold mb-2">Manage Investors</h3>
                  <p className="text-[#94A3B8] text-sm mb-4 pr-12">Review applications and manage investor accounts</p>
                  {stats?.pending_applications > 0 && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EF4444]"></span>
                      </span>
                      <span className="text-[#EF4444] text-xs font-bold uppercase tracking-wider">{stats.pending_applications} Pending</span>
                    </div>
                  )}
                </div>
              </button>

              <button onClick={() => navigate('/admin/assets')} className="group premium-card p-6 hover:scale-[1.02] transition-all text-left cursor-pointer relative overflow-hidden" data-testid="goto-assets-btn">
                <div className="absolute top-0 right-0 p-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div className="relative z-10">
                  <h3 className="font-['Outfit'] text-xl font-bold mb-2">Manage Assets</h3>
                  <p className="text-[#94A3B8] text-sm mb-4 pr-12">Create, edit, and manage investment assets</p>
                  <div className="text-[#D4AF37] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    View Assets &rarr;
                  </div>
                </div>
              </button>

              <button onClick={() => navigate('/admin/commitments')} className="group premium-card p-6 hover:scale-[1.02] transition-all text-left cursor-pointer relative overflow-hidden" data-testid="goto-commitments-btn">
                <div className="absolute top-0 right-0 p-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <Clock className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div className="relative z-10">
                  <h3 className="font-['Outfit'] text-xl font-bold mb-2">Review Commitments</h3>
                  <p className="text-[#94A3B8] text-sm mb-4 pr-12">Approve or reject investment commitments</p>
                  <div className="flex flex-col gap-2 items-start">
                    {stats?.pending_commitments > 0 && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F59E0B] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F59E0B]"></span>
                        </span>
                        <span className="text-[#F59E0B] text-xs font-bold uppercase tracking-wider">{stats.pending_commitments} New</span>
                      </div>
                    )}
                    {stats?.processing_payments > 0 && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-full">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3B82F6] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3B82F6]"></span>
                        </span>
                        <span className="text-[#3B82F6] text-xs font-bold uppercase tracking-wider">{stats.processing_payments} Paid</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>

              <button onClick={() => navigate('/admin/payouts')} className="group premium-card p-6 hover:scale-[1.02] transition-all text-left cursor-pointer relative overflow-hidden" data-testid="goto-payouts-btn">
                <div className="absolute top-0 right-0 p-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                    <DollarSign className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                </div>
                <div className="relative z-10">
                  <h3 className="font-['Outfit'] text-xl font-bold mb-2">Process Payouts</h3>
                  <p className="text-[#94A3B8] text-sm mb-4 pr-12">Manage and mark payouts as paid</p>
                  {stats?.pending_payouts > 0 ? (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]"></span>
                      </span>
                      <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider">{stats.pending_payouts} Due</span>
                    </div>
                  ) : (
                    <div className="text-[#D4AF37] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      View Payouts &rarr;
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </AppLayout>
  );
}
